import React from "react";
import { useI18n } from "i18n";
import { Accordion } from "@remo-co/ui-core/src/components/Accordion";
import { AccordionDetails } from "@remo-co/ui-core/src/components/AccordionDetails";
import { AccordionSummary } from "@remo-co/ui-core/src/components/AccordionSummary";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { KeyboardArrowUp } from "@remo-co/ui-core/src/icons/KeyboardArrowUp";
import classNames from "classnames";
import EventList from "./EventList";
import { useStyles } from "./styles";
import { IEvent } from "../types";

interface IEventListToggleProps {
  events: IEvent[];
  editable: boolean;
  title: string;
  ended?: boolean;
  hasNextPage?: boolean;
  isLoading?: boolean;
  fetchNextPage?: () => void;
  totalCount?: number;
  refetch?: () => void;
}

const EventListToggle = ({
  events,
  editable,
  title,
  ended = false,
  fetchNextPage,
  hasNextPage = false,
  isLoading,
  totalCount,
  refetch,
}: IEventListToggleProps) => {
  const { t } = useI18n(["event"]);
  const [isExpanded, setIsExpanded] = React.useState(true);
  const styles = useStyles();

  const toggleSection = () => {
    setIsExpanded(!isExpanded);
  };

  if (events && events.length) {
    return (
      <Accordion
        expanded={isExpanded}
        onChange={toggleSection}
        classes={{
          root: styles.root,
          expanded: styles.expanded,
        }}
      >
        <AccordionSummary
          classes={{
            root: styles.summary,
            content: styles.summaryContent,
            expandIconWrapper: styles.summaryExpandIcon,
          }}
          expandIcon={
            <KeyboardArrowUp
              className={classNames(styles.expandIcon, {
                expanded: isExpanded,
              })}
            />
          }
          className="summary"
        >
          <Typography variant="h4">
            {title} ({events.length}
            {totalCount ? ` / ${totalCount}` : ""}){" "}
          </Typography>
        </AccordionSummary>
        <AccordionDetails classes={{ root: styles.detailsRoot }}>
          <EventList
            events={events}
            editable={editable}
            refetch={refetch}
            ended={ended}
          />
        </AccordionDetails>
        {fetchNextPage && (
          <Container
            justifyContent="center"
            flex
            fullWidth
            data-testid="load-more-container"
            className={styles.loadMoreContainer}
          >
            <Button
              onClick={fetchNextPage}
              color="blue"
              data-testid="load-more-button"
              loading={isLoading}
              disabled={!hasNextPage}
            >
              {hasNextPage
                ? t("event:load.more")
                : t("event:all.events.loaded")}
            </Button>
          </Container>
        )}
      </Accordion>
    );
  }

  return null;
};

export default EventListToggle;
